/**
 * Display helpers for the deletion window: a super admin delete hides the
 * item and the API purges it at `purge_at` (DELETION_RETENTION_HOURS later).
 * Until then it sits under Chat History > Recently deleted with a Restore
 * button. All inputs are ISO 8601 UTC strings from the API.
 */

const dateTime: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
};

/** "16 Oct, 14:32" in the viewer's locale and time zone. */
export const formatWindowTime = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleString(undefined, dateTime) : "";

/** "23 hours left to restore", counting down to the purge. */
export const timeLeftToRestore = (purgeAt?: string | null): string => {
  if (!purgeAt) return "";
  const ms = new Date(purgeAt).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return "Being removed now";
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) return `${hours} hour${hours === 1 ? "" : "s"} left to restore`;
  const minutes = Math.max(1, Math.floor(ms / 60_000));
  return `${minutes} minute${minutes === 1 ? "" : "s"} left to restore`;
};

/** "24 hours" / "1 hour" / "30 minutes" for a retention given in hours. */
export const describeRetention = (hours?: number | null): string => {
  const h = typeof hours === "number" && hours > 0 ? hours : 24;
  if (h < 1) {
    const minutes = Math.max(1, Math.round(h * 60));
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  const rounded = Number.isInteger(h) ? h : Math.round(h * 10) / 10;
  return `${rounded} hour${rounded === 1 ? "" : "s"}`;
};

/** True while the item can still be restored (its purge time is ahead). */
export const canStillRestore = (purgeAt?: string | null): boolean =>
  !!purgeAt && new Date(purgeAt).getTime() > Date.now();

/**
 * The message for a failed restore. A 409 means the 24 hours ran out and
 * the item is already being removed, which is worth saying in plain words.
 */
export const restoreErrorMessage = (error: unknown, fallback: string): string => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 409
    ? "Too late to restore: the 24 hours have passed and it is being removed."
    : fallback;
};
